import type React from 'react';
import {Button, IconButton, Stack, Typography} from '@mui/material';
import {Add, Close} from '@mui/icons-material';
import {ClickToEdit} from '~/components/ClickToEdit/ClickToEdit';

type Field<T> = {key: keyof T & string; placeholder: string; secondary?: boolean};

type Props<T extends {id: string}> = {
    items: T[];
    fields: Field<T>[];
    readOnly: boolean;
    addLabel: string;
    emptyText: string;
    /** Replace the whole list; the caller saves it with Undo. */
    onChange: (items: T[], message: string) => void;
    /** A blank new item. */
    create: () => T;
};

/** A list whose items edit in place, with Add and Remove. Saving (and Undo) is the caller's. */
export function EditableList<T extends {id: string}>({items, fields, readOnly, addLabel, emptyText, onChange, create}: Props<T>) {
    const update = (id: string, key: keyof T, value: string) =>
        onChange(items.map(i => (i.id === id ? {...i, [key]: value} : i)), 'Updated.');

    return (
        <Stack gap={1}>
            {items.length === 0 && <Typography variant="body2" color="text.secondary">{emptyText}</Typography>}
            {items.map(item => (
                <Stack key={item.id} direction="row" gap={1} alignItems="flex-start">
                    <Stack sx={{flex: 1, minWidth: 0}}>
                        {fields.map(f => (
                            <ClickToEdit key={f.key} text={String(item[f.key] ?? '')} readOnly={readOnly} placeholder={f.placeholder}
                                         typographyProps={{variant: 'body2', color: f.secondary ? 'text.secondary' : 'text.primary'}}
                                         onSave={v => {
                                             const value = v.trim();
                                             if (value === item[f.key] || (!value && !f.secondary)) return;
                                             update(item.id, f.key, value);
                                         }}/>
                        ))}
                    </Stack>
                    {!readOnly && (
                        <IconButton size="small" aria-label="Remove" onClick={() => onChange(items.filter(i => i.id !== item.id), 'Removed.')}>
                            <Close fontSize="small"/>
                        </IconButton>
                    )}
                </Stack>
            ))}
            {!readOnly && (
                <Button size="small" startIcon={<Add/>} sx={{alignSelf: 'flex-start'}}
                        onClick={() => onChange([...items, create()], 'Added.')}>
                    {addLabel}
                </Button>
            )}
        </Stack>
    );
}
